import { useState, useContext } from 'react';
import { lineItemsToOrderData, getOrders } from '../../../selectors';
import AppContext from '../../../AppContext';
import OrderStore from '../../../store/OrderStore';
import { orderStatus } from '../../../constants';
import _ from 'lodash';
import { getSalesforceConnection } from '../../../utils/getSalesforceConnection';
import { initializeViewedOrdersArray } from '../../../utils/viewedOrdersArray';
import { getTranslateByScope } from '../../../i18n';
import { isUnauthorized } from '../../../utils/salesforce';

const translate = getTranslateByScope('app.sync');
const useSyncOrdersSalesforce = () => {
  const [loading, setLoading] = useState(false);
  const context = useContext(AppContext);
  const orderStore = new OrderStore('orderDb');

  const formatData = (lineItem) => {
    const formattedData = {
      customerId: lineItem.customer_id,
      deliveryAddress: lineItem.shipping_address,
      billingAddress: lineItem.billing_address,
      discount: lineItem.discount,
      notes: lineItem.comment,
      orderNumber: lineItem.external_order_id,
      agentId: lineItem.agent_id,
      orderDate: lineItem.order_date,
      products: lineItemsToOrderData(context, lineItem.line_items),
      orderType: {
        label: lineItem.order_type,
        value: lineItem.order_type,
      },
      discount1: lineItem.discount1,
      discount2: lineItem.discount2,
      discount3: lineItem.discount3,
      status: lineItem.draft ? 'draft' : 'done',
      season: lineItem.season || lineItem.line_items[0].season,
      minDeliveryDate: lineItem.min_delivery_date,
      id: lineItem.order_id,
      salesforce_id: lineItem.salesforce_id,
    };
    return formattedData;
  };

  const onSuccess = (lineItems) => {
    setLoading(false);
    const mapOrderIds = lineItems.map((l) => l.id);
    const localOrders = getOrders(context);
    const mapLocalOrderIds = localOrders.map((l) => l.doc.id);
    const orderData = lineItems.map((l) => {
      if (mapLocalOrderIds.includes(l.order_id)) return null;
      if (!l.line_items || l.line_items.length === 0) return null;
      const data = formatData(l);

      if (!data.products) {
        return null;
      }

      return data;
    });

    const mapLocalOrders = localOrders
      ? localOrders.map((l) => (mapOrderIds.includes(l.doc.id) ? null : l.doc))
      : [];

    const ordersToSet = _.compact([...orderData, ...mapLocalOrders]);

    const updatedProductList = ordersToSet.map((order) => {
      let d = order;
      const iteratedOrder = _.find(lineItems, { order_id: order.id });

      if (!iteratedOrder) {
        return d;
      }

      const formattedProductList = lineItemsToOrderData(
        context,
        iteratedOrder.line_items
      );

      if (!_.isEqual(d.products, formattedProductList)) {
        d = { ...d, products: formattedProductList };
      }

      d = {
        ...d,
        status: iteratedOrder.draft ? orderStatus.draft : orderStatus.done,
        salesforce_id: iteratedOrder.salesforce_id,
      };

      return d;
    });

    const reload = () => {
      window.location.reload();
    };

    const orderIdArray = updatedProductList
      .filter((order) => order.status === orderStatus.draft)
      .map((order) => order.id);
    initializeViewedOrdersArray(orderIdArray);
    context.actions.setCurrentOrder(null);
    orderStore.bulkUpsert(updatedProductList, reload);
  };

  const syncOrdersSalesforce = async () => {
    let retries = 0;

    const inner = async () => {
      if (retries === 5) {
        throw new Error('Max out retries');
      }
      try {
        const conn = await getSalesforceConnection(
          context.entities.session,
          retries > 0
        );
        let url = '/orders/lists';
        if (context.state.lowa) {
          url += '?CustomerNo=' + context.state.lowaCustomer.CustomerNo__c;
        }
        const data = await conn.apex.get(url);
        return data;
      } catch (error) {
        if (isUnauthorized(error)) {
          retries++;

          const data = await inner();
          return data;
        }
      }
    };

    const data = await inner();

    return data;
  };

  const handleOnClick = async () => {
    try {
      if (loading) return;
      setLoading(true);
      const sfOrders = await syncOrdersSalesforce();
      onSuccess(sfOrders);
    } catch (error) {
      setLoading(false);

      context.actions.setToast({
        title: translate('sync.syncAuthError.title'),
        text: translate('sync.syncAuthError.description'),
      });
    }
  };

  return { loading, handleOnClick };
};

export default useSyncOrdersSalesforce;
